<template>
  <div class="tw-w-full tw-bg-black tw-rounded-lg tw-overflow-hidden tw-relative" style="aspect-ratio: 16 / 9;">
    <!-- Embedded providers (YouTube / Vimeo) -->
    <iframe
      v-if="embedUrl"
      :src="embedUrl"
      class="tw-absolute tw-inset-0 tw-w-full tw-h-full"
      frameborder="0"
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
      allowfullscreen
    ></iframe>

    <!-- Direct file / mp4 -->
    <video
      v-else-if="fileUrl"
      :src="fileUrl"
      :poster="video?.thumbnail_url || undefined"
      controls
      controlsList="nodownload noplaybackrate"
      disablePictureInPicture
      @contextmenu.prevent
      class="tw-absolute tw-inset-0 tw-w-full tw-h-full tw-object-contain tw-bg-black"
    ></video>

    <!-- Nothing to play -->
    <div v-else class="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-text-gray-400">
      <icon icon="solar:videocamera-record-broken-bold-duotone" class="tw-text-5xl" />
    </div>
  </div>
</template>

<script setup>
import { computed, defineProps } from 'vue';

const props = defineProps({
    video: { type: Object, default: null }
});

// Provider embeds resolve to an iframe URL; everything else falls back to <video>.
const embedUrl = computed(() => {
    const v = props.video;
    if (!v || v.source_type === 'upload') return null;

    const provider = v.provider;
    const url = v.external_url || v.playback_url || '';

    if (provider === 'youtube') {
        const id = extractYouTubeId(url);
        return id ? `https://www.youtube.com/embed/${id}` : null;
    }
    if (provider === 'vimeo') {
        const id = extractVimeoId(url);
        return id ? `https://player.vimeo.com/video/${id}` : null;
    }
    return null; // mp4 / direct link handled by <video>
});

const fileUrl = computed(() => {
    const v = props.video;
    if (!v) return null;
    if (v.source_type === 'upload') return v.playback_url || null;
    if (v.provider === 'mp4') return v.external_url || v.playback_url || null;
    return null;
});

function extractYouTubeId(url) {
    if (!url) return null;
    const patterns = [
        /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/shorts\/)([\w-]{11})/
    ];
    for (const p of patterns) {
        const m = url.match(p);
        if (m) return m[1];
    }
    return null;
}

function extractVimeoId(url) {
    if (!url) return null;
    const m = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
    return m ? m[1] : null;
}
</script>
